import { FC, useEffect, useMemo, useState } from "react";
import useRouteQueryParams from "../../../../../common/hooks/use-route-query-params";
import usePagination from "../../../../../common/hooks/use-pagination";
import HeadingComponent from "../../../../../common/components/heading/heading.component";
import ListingSortSelectComponent from "../../../../../common/components/listing/filter/sort/select/listing-sort-select.component";
import CardComponent from "../../../../../common/components/card/card.component";
import PaginationComponent from "../../../../../common/components/pagination/pagination.component";
import { useAppContext } from "../../../../../context/app.context";
import DateRange from "../../../../../common/types/date-range";
import useIsComponentMounted from "../../../../../common/hooks/use-is-component-mounted";
import ButtonComponent from "../../../../../common/components/button/button.component";
import notificationService from "../../../../../common/utils/notification/notification.service";
import fileDownloadService from "../../../../../common/utils/file-download/file-download.service";
import DateRangeInputComponent from "../../../../../common/components/form/input/date-range/date-range-input.component";
import BillingsCargoOrderListingViewBasicProps from "../common/types/billings-cargo-order-listing-view-basic-props";
import BillingsCargoOrderListingItem from "../common/types/billings-cargo-order-listing-item";
import BillingsCargoOrderListingFilter, {
  BillingsCargoOrderListingOrderStartDateFilter,
} from "../common/types/billings-cargo-order-listing-filter";
import BillingsCargoOrderListingSortKey from "../common/types/billings-cargo-order-listing-sort-key";
import BillingsCargoOrderListingSortSelectOption from "../common/types/billings-cargo-order-listing-sort-select-option";
import billingsCargoOrderListingRequestFactory from "../common/billings-cargo-order-listing-request.factory";
import BillingsCargoOrderListingFilterType from "../common/types/billings-cargo-order-listing-filter-type";
import billingsCargoOrderListingReportRequestFactory from "../common/billings-cargo-order-listing-report-request.factory";
import BillingsCargoOrderListingStatusLegendComponent from "../common/status-legend/billings-cargo-order-listing-status-legend.component";
import BillingsCargoOrderListingFiltersSelectComponent from "../common/filters/select/billings-cargo-order-listing-filters-select.component";
import BillingsCargoOrderListingFiltersBadgeListComponent from "../common/filters/list/billings-cargo-order-listing-filters-badge-list.component";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsCargoOrderListingByCargoRouteQueryParams from "./common/types/billings-cargo-order-listing-by-cargo-route-query-params";
import billingsCargoOrderListingByCargoRouteQueryParamsService from "./common/billings-cargo-order-listing-by-cargo-route-query-params.service";
import billingsCargoOrderListingSortHelper from "../common/billings-cargo-order-listing-sort.helper";
import billingsCargoOrderListingByCargoFactory from "./common/billings-cargo-order-listing-by-cargo.factory";
import BillingsCargoOrderListingTableComponent from "../common/table/billings-cargo-order-listing-table.component";
import BillingsCargoOrderListingBillingsAcceptData from "../common/types/billings-cargo-order-listing-billings-accept-data";
import BillingsCargoOrderListingBillingsAcceptComponent from "../common/billings-accept/billings-cargo-order-listing-billings-accept.component";
import useAbort from "../../../../../common/hooks/use-abort";
import useCargoCompanyOrderBillingsList from "../../../../../common/services/cargo-company/order-billings/list/use-cargo-company-order-billings-list";
import CargoCompanyOrderBillingsList from "../../../../../common/services/cargo-company/order-billings/list/cargo-company-order-billings-list";
import CargoCompanyOrderBillingsListParams from "../../../../../common/services/cargo-company/order-billings/list/cargo-company-order-billings-list-params";
import billingService from "../../../../../common/services/billings/billings.service";
import BillingsCargoTaxiBulkUpdateStatusParams from "../../../../../common/services/billings/cargo-taxi/bulk-update-status/billings-cargo-taxi-bulk-update-status-params";
import cargoCompanyService from "../../../../../common/services/cargo-company/cargo-company.service";
import CargoCompanyOrderBillingsDownloadParams from "../../../../../common/services/cargo-company/order-billings/download/cargo-company-order-billings-download-params";
import CargoCompanyOrderBillingsDownload from "../../../../../common/services/cargo-company/order-billings/download/cargo-company-order-billings-download";
import BillingsCargoOrderListingItemBillingStatus from "../common/types/billings-cargo-order-listing-item-billing-status";
import BillingStatus from "../../../types/billing-status";

type BillingsCargoOrderListingByCargoProps =
  BillingsCargoOrderListingViewBasicProps;

const BillingsCargoOrderListingByCargoComponent: FC<
  BillingsCargoOrderListingByCargoProps
> = (props) => {
  const isComponentMounted = useIsComponentMounted();
  const { user, selectedAppLanguage } = useAppContext();

  const cargoCompanyUuid: string =
    user?.aspects.cargoOfficer?.cargoCompanyUuid ??
    user?.aspects.dispatcher?.cargoCompanyUuid!;

  const translations =
    billingsTranslationsHelper.getCargoOrderBillingsListingTranslations();

  const [routeQueryParams, setRouteQueryParams] =
    useRouteQueryParams<BillingsCargoOrderListingByCargoRouteQueryParams>();

  const [isAcceptBillingsEnabled, setIsAcceptBillingsEnabled] = useState(false);

  const [selectedBillingsAcceptData, setSelectedBillingsAcceptData] = useState<
    BillingsCargoOrderListingBillingsAcceptData[]
  >([]);

  const enableAcceptingBillings = () => setIsAcceptBillingsEnabled(true);

  const disableAcceptingBillings = () => setIsAcceptBillingsEnabled(false);

  const [listingItems, setListingItems] = useState<
    BillingsCargoOrderListingItem[]
  >([]);
  const [totalResults, setTotalResults] = useState(0);

  const [isReportFetching, setIsReportFetching] = useState(false);

  const cargoCompanyOrderBillingsList = useCargoCompanyOrderBillingsList();
  const cargoCompanyOrderBillingsListAbort = useAbort();

  const billingsAcceptCargoTaxiAbort = useAbort();

  const downloadAbort = useAbort();

  const [filters, setFilters] = useState<BillingsCargoOrderListingFilter[]>(
    () =>
      billingsCargoOrderListingByCargoRouteQueryParamsService.getFilters(
        routeQueryParams,
      ),
  );

  const [selectedSortKey, setSelectedSortKey] =
    useState<BillingsCargoOrderListingSortKey | null>(
      () =>
        billingsCargoOrderListingByCargoRouteQueryParamsService.getSortKey(
          routeQueryParams,
        ) ?? null,
    );

  const sortSelectOptions: BillingsCargoOrderListingSortSelectOption[] =
    useMemo(
      () => billingsCargoOrderListingSortHelper.getSelectOptions(),
      [selectedAppLanguage],
    );

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: totalResults,
    defaultPageSize:
      billingsCargoOrderListingByCargoRouteQueryParamsService.getPageSize(
        routeQueryParams,
      ),
    defaultPage:
      billingsCargoOrderListingByCargoRouteQueryParamsService.getPage(
        routeQueryParams,
      ),
  });

  const onListingFetchSuccess = (
    responseData: CargoCompanyOrderBillingsList,
  ) => {
    const listingItems =
      billingsCargoOrderListingByCargoFactory.createListingItems(
        responseData.data,
      );

    setListingItems(listingItems);

    setTotalResults(responseData.totalCount);
  };

  const fetchListing = () => {
    const params: CargoCompanyOrderBillingsListParams =
      billingsCargoOrderListingRequestFactory.createRequest(
        page,
        pageSize,
        cargoCompanyUuid,
        filters,
        selectedSortKey,
      );

    cargoCompanyOrderBillingsList.load(
      params,
      cargoCompanyOrderBillingsListAbort.signal,
    );
  };

  useEffect(() => {
    if (!cargoCompanyOrderBillingsList.data) {
      return;
    }
    onListingFetchSuccess(cargoCompanyOrderBillingsList.data);
  }, [cargoCompanyOrderBillingsList.data]);

  useEffect(() => {
    if (!page || !pageSize) {
      return;
    }

    fetchListing();
    return cargoCompanyOrderBillingsListAbort.revoke;
  }, [page, pageSize, filters, selectedSortKey]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    const queryParams =
      billingsCargoOrderListingByCargoRouteQueryParamsService.createRouteQueryParams(
        filters,
        selectedSortKey,
        page,
        pageSize,
      );

    setRouteQueryParams(queryParams);
  }, [filters, selectedSortKey, page, pageSize]);

  const addNewFilter = (newFilter: BillingsCargoOrderListingFilter) => {
    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const deleteFilter = (index: number) => {
    const newFilters = filters.filter((filter, _index) => _index !== index);

    setFilters(newFilters);
    setPage(1);
  };

  const deleteAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const onPageChange = (page: number) => {
    setPage(page);
    window.scroll({ top: 0, behavior: "smooth" });
  };

  const onPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  const selectedSortSelectOption = useMemo(() => {
    return (
      sortSelectOptions.find((item) => item.value === selectedSortKey) ?? null
    );
  }, [selectedSortKey, selectedAppLanguage]);

  const startDateFilterValue = filters.find(
    (filter) =>
      filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
  )?.value as
    | BillingsCargoOrderListingOrderStartDateFilter["value"]
    | undefined;

  const onStartDateFilterValueChange = (dateRange: DateRange | null) => {
    if (!dateRange) {
      const newFilters = filters.filter(
        (filter) =>
          filter.type !== BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      );

      setFilters(newFilters);
      return;
    }

    const isFilterExists = !!filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
    );

    const newFilter: BillingsCargoOrderListingOrderStartDateFilter = {
      type: BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      value: {
        from: dateRange.from!,
        to: dateRange.to!,
      },
    };

    if (isFilterExists) {
      const newFilters = [
        ...filters.filter(
          (filter) =>
            filter.type !==
            BillingsCargoOrderListingFilterType.ORDER_START_DATE,
        ),
        newFilter,
      ];

      setFilters(newFilters);
      setPage(1);
      return;
    }

    setFilters((curr) => [...curr, newFilter]);
    setPage(1);
  };

  const onReportFetchSuccess = async (
    response: CargoCompanyOrderBillingsDownload,
  ) => {
    fileDownloadService.downloadFromBlob(response.data, response.filename);
  };

  const onReportFetchFailure = () => {
    notificationService.error(
      translations.report.failureDownloadingNotificationText,
    );
  };

  const fetchReport = () => {
    setIsReportFetching(true);

    const params: CargoCompanyOrderBillingsDownloadParams =
      billingsCargoOrderListingReportRequestFactory.createRequest(
        filters,
        selectedSortKey,
        cargoCompanyUuid,
      );

    cargoCompanyService
      .fetchReport(params, downloadAbort.signal)
      .then(onReportFetchSuccess)
      .catch(onReportFetchFailure)
      .finally(() => setIsReportFetching(false));
  };

  const onReportDownloadButtonClick = () => {
    fetchReport();
  };

  const ReportDownloadButton = (
    <ButtonComponent
      onClick={onReportDownloadButtonClick}
      type="primary"
      isLoading={isReportFetching}
      title={translations.report.downloadButtonTitle}
      idForTesting="billings-cargo-order-listing-by-cargo-report-download-button"
    >
      {translations.report.downloadButtonText}
    </ButtonComponent>
  );

  const onAcceptBillingsButtonClick = () =>
    isAcceptBillingsEnabled
      ? disableAcceptingBillings()
      : enableAcceptingBillings();

  const AcceptBillingsButton = (
    <ButtonComponent
      onClick={onAcceptBillingsButtonClick}
      type={isAcceptBillingsEnabled ? "success" : "primary"}
      title={
        isAcceptBillingsEnabled
          ? translations.header.acceptBillingsDisableTitle
          : translations.header.acceptBillingsEnableTitle
      }
      idForTesting="billings-cargo-order-listing-by-raily-accept-billings-button"
    >
      {translations.header.acceptBillingsLabel}
    </ButtonComponent>
  );

  const onAcceptBillingsSuccess = () => {
    fetchListing();

    notificationService.success(
      translations.billingsAccept.successNotificationLabel,
    );
  };

  const onAcceptBillingsFailure = () => {
    notificationService.error(
      translations.billingsAccept.failureNotificationLabel,
    );
  };

  const acceptBillings = () => {
    const params: BillingsCargoTaxiBulkUpdateStatusParams = {
      billingIds: selectedBillingsAcceptData.map((item) => item.uuid),
      status: BillingStatus.ACCEPTED,
    };

    billingService
      .updateBulkStatusCargoTaxiBillings(
        params,
        billingsAcceptCargoTaxiAbort.signal,
      )
      .then(onAcceptBillingsSuccess)
      .catch(onAcceptBillingsFailure)
      .finally(() => {
        setSelectedBillingsAcceptData([]);
        setIsAcceptBillingsEnabled(false);
      });
  };

  const isReportDownloadButtonVisible = !!cargoCompanyUuid;
  const isAcceptBillingsButtonVisible = !!cargoCompanyUuid;

  return (
    <div className="billings_cargo_order_listing">
      <HeadingComponent
        title={translations.header.headingText}
        actions={[
          ...props.actionButtons,
          isReportDownloadButtonVisible && ReportDownloadButton,
          isAcceptBillingsButtonVisible && AcceptBillingsButton,
        ]}
      />
      <BillingsCargoOrderListingStatusLegendComponent
        cargoCompanyUuid={cargoCompanyUuid}
        visibleStatuses={[
          BillingsCargoOrderListingItemBillingStatus.CREATED,
          BillingsCargoOrderListingItemBillingStatus.REJECTED,
          BillingsCargoOrderListingItemBillingStatus.REOPENED,
          BillingsCargoOrderListingItemBillingStatus.REOPEN_REQUEST_SENT,
        ]}
      />
      <div className="billings_cargo_order_listing_tools">
        <div className="d-flex">
          <BillingsCargoOrderListingFiltersSelectComponent
            filters={filters}
            onAddNewFilter={addNewFilter}
          />
          <DateRangeInputComponent
            date={startDateFilterValue ?? null}
            onChange={onStartDateFilterValueChange}
            classNames={{ root: "ml-2" }}
            placeholder={
              translations.filters.searchByStartDateSelectInputPlaceholder
            }
            idForTesting="billings-cargo-order-listing-by-cargo-date-range"
          />
        </div>
        <ListingSortSelectComponent
          onChange={(option) => setSelectedSortKey(option?.value!)}
          options={sortSelectOptions}
          value={selectedSortSelectOption}
          idForTesting="billings-cargo-order-listing-by-cargo-sort"
        />
      </div>
      <BillingsCargoOrderListingFiltersBadgeListComponent
        filters={filters}
        onDeleteFilterClick={deleteFilter}
        onDeleteAllFiltersButtonClick={deleteAllFilters}
      />
      <CardComponent classNames={{ root: "mt-4" }}>
        <BillingsCargoOrderListingTableComponent
          listingItems={listingItems}
          isError={cargoCompanyOrderBillingsList.isError}
          isLoading={cargoCompanyOrderBillingsList.isLoading}
          isAcceptBillingsEnabled={isAcceptBillingsEnabled}
          selectedBillingsAcceptData={selectedBillingsAcceptData}
          onSelectedBillingsAcceptDataChange={setSelectedBillingsAcceptData}
        />
        <div className="billings_cargo_order_listing__pagination_wrapper">
          <PaginationComponent
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            page={page}
            pageSize={pageSize}
            totalResults={totalResults}
          />
        </div>
      </CardComponent>
      {isAcceptBillingsEnabled && (
        <BillingsCargoOrderListingBillingsAcceptComponent
          billingsAcceptData={selectedBillingsAcceptData}
          onSubmit={acceptBillings}
          onCancel={() => {
            setSelectedBillingsAcceptData([]);
            setIsAcceptBillingsEnabled(false);
          }}
        />
      )}
    </div>
  );
};

export default BillingsCargoOrderListingByCargoComponent;
